import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export interface BrandingConfig {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string | null;
    buttonColor: string | null;
    navTextColor: string | null;
    logoUrl: string | null;
    appName: string;
    faviconUrl: string | null;
    splashImageUrl: string | null;
    supportEmail: string | null;
    serviceNoun: string | null;        // e.g. "pickup" — null means use generic "service" language
    serviceDescription: string | null; // home card subtitle — null falls back to generic
    contractorNoun: string | null;     // e.g. "Runner" — null means use generic "Contractor" language. Display-only.
    logoLinkUrl: string | null;        // when set, clicking the header logo opens this URL in a new tab
    stripePublishableKey: string | null; // from the API, same source as the server's Stripe secret key — used to init Stripe.js
}

const DEFAULTS: BrandingConfig = {
    primaryColor: '#1D9E75',
    secondaryColor: '#0F6E56',
    accentColor: null,
    buttonColor: null,
    navTextColor: null,
    logoUrl: null,
    appName: '',
    faviconUrl: null,
    splashImageUrl: null,
    supportEmail: null,
    serviceNoun: null,
    serviceDescription: null,
    contractorNoun: null,
    logoLinkUrl: null,
    stripePublishableKey: null,
};

const BrandingCtx = createContext<BrandingConfig>(DEFAULTS);

// Keyed by hostname so BoxRunner and Fobbus caches never collide in the same browser.
function cacheKey() { return `br_branding_${window.location.hostname}`; }

function loadCache(): BrandingConfig | null {
    try {
        const raw = sessionStorage.getItem(cacheKey());
        return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : null;
    } catch { return null; }
}

function applyTokens(b: BrandingConfig) {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', b.primaryColor);
    root.style.setProperty('--color-secondary', b.secondaryColor);
    root.style.setProperty('--color-accent', b.accentColor ?? b.secondaryColor);
    root.style.setProperty('--color-button', b.buttonColor ?? b.secondaryColor);
    root.style.setProperty('--color-nav-text', b.navTextColor ?? '#ffffff');

    if (b.faviconUrl) {
        const link = (document.querySelector("link[rel~='icon']") ||
            Object.assign(document.createElement('link'), { rel: 'icon' })) as HTMLLinkElement;
        link.href = b.faviconUrl;
        document.head.appendChild(link);
    }

    if (b.appName) document.title = b.appName;
}

// Runs once (lazy useState init) — synchronously before first render so CSS variables
// are already correct when the component tree paints.
function getInitialBranding(): BrandingConfig {
    const cached = loadCache();
    const initial = cached ?? DEFAULTS;
    applyTokens(initial);
    return initial;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
    const [branding, setBranding] = useState<BrandingConfig>(getInitialBranding);

    useEffect(() => {
        const base = process.env.REACT_APP_API_URL || 'http://10.0.0.175:5000';
        fetch(`${base}/api/tenant/branding`)
            .then(r => r.ok ? r.json() : null)
            .then((data: BrandingConfig | null) => {
                if (!data) return;
                const merged = { ...DEFAULTS, ...data };
                setBranding(merged);
                applyTokens(merged);
                try { sessionStorage.setItem(cacheKey(), JSON.stringify(data)); } catch {}
            })
            .catch(() => {/* keep cached or defaults */});
    }, []);

    return <BrandingCtx.Provider value={branding}>{children}</BrandingCtx.Provider>;
}

export const useBranding = () => useContext(BrandingCtx);
